import { isEqual } from 'helpers/global.helper/global.helper'
import React, { ReactElement, ReactNode, useEffect, useMemo, useState } from 'react'
import uiConfigStore from 'store/uiConfig/uiConfig'
import SearchField from 'ui/atoms/SearchField/SearchField'
import MonacoStableDiffEditor from 'ui/components/MonacoStableDiffEditor/MonacoStableDiffEditor'
import { useSnapshot } from 'valtio'
import {
  CONFIG_MALFORMED_RESOLUTION_MESSAGE,
  CONFIG_NOT_FOUND_MESSAGE,
  CONFIG_UNAVAILABLE_MESSAGE,
} from '../uiConfigDiagnosticsMessages'
import {
  getResolutionChain,
  getResolutionStepDescription,
  getResolutionStepSource,
  getResolutionStepSummary,
  type ResolutionStepId,
  stringifyTrackedFields,
  TRACKED_FIELDS,
} from './UiConfigResolutionInspector.helpers'
import styles from './UiConfigResolutionInspector.module.less'

type Props = {
  uid: UiConfigUid
}

type MutationStepMatch = {
  matches: boolean
  matchedChangedFieldNames: string[]
}

const FILTER_MIN_LENGTH = 3
const FILTER_DEBOUNCE_MS = 2000

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function getChangedSubset(rawValue: unknown, stepValue: unknown): unknown {
  if (isEqual(rawValue, stepValue)) {
    return undefined
  }

  if (isRecord(rawValue) && isRecord(stepValue)) {
    const changed: Record<string, unknown> = {}

    // Iterate the union of keys so that deletions (keys in rawValue but absent from stepValue)
    // are represented explicitly, allowing field-name filtering to surface steps that remove props.
    const allKeys = new Set([...Object.keys(rawValue), ...Object.keys(stepValue)])

    for (const key of allKeys) {
      if (key in rawValue && !(key in stepValue)) {
        // Key was deleted in this step — use null sentinel so collectAllFieldNames can surface the removed key name
        changed[key] = null
      } else {
        const nestedChanged = getChangedSubset(rawValue[key], stepValue[key])
        if (nestedChanged !== undefined) {
          changed[key] = nestedChanged
        }
      }
    }

    return Object.keys(changed).length > 0 ? changed : stepValue
  }

  return stepValue
}

function getChangedFieldsFromRaw(
  rawUiConfig: UiConfig,
  stepUiConfig: UiConfig
): Partial<Record<(typeof TRACKED_FIELDS)[number], unknown>> {
  const changedFields: Partial<Record<(typeof TRACKED_FIELDS)[number], unknown>> = {}

  for (const field of TRACKED_FIELDS) {
    const changedValue = getChangedSubset(rawUiConfig[field], stepUiConfig[field])
    if (changedValue !== undefined) {
      changedFields[field] = changedValue
    }
  }

  return changedFields
}

function collectAllFieldNames(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.flatMap((item) => collectAllFieldNames(item))
  }

  if (isRecord(value)) {
    return Object.entries(value).flatMap(([key, nestedValue]) => {
      return [key, ...collectAllFieldNames(nestedValue)]
    })
  }

  return []
}

function renderHighlightedText(text: string, query: string): ReactNode {
  const normalizedQuery = query.trim()
  if (!normalizedQuery) {
    return text
  }

  const lowerText = text.toLowerCase()
  const lowerQuery = normalizedQuery.toLowerCase()
  const parts: ReactNode[] = []
  let cursor = 0
  let matchIndex = lowerText.indexOf(lowerQuery, cursor)
  let markKey = 0

  while (matchIndex >= 0) {
    if (matchIndex > cursor) {
      parts.push(text.slice(cursor, matchIndex))
    }
    parts.push(
      <mark
        key={`resolution-match-${markKey}`}
        className={styles.UiConfigResolutionInspector_MatchHighlight}
        data-testid="resolution-match-highlight"
      >
        {text.slice(matchIndex, matchIndex + normalizedQuery.length)}
      </mark>
    )
    markKey += 1
    cursor = matchIndex + normalizedQuery.length
    matchIndex = lowerText.indexOf(lowerQuery, cursor)
  }

  if (cursor < text.length) {
    parts.push(text.slice(cursor))
  }

  return <>{parts}</>
}

function renderStepSummary(summary: string, query: string): ReactNode {
  const summaryMatch = summary.match(/^(.*?)(\+\d+)(\s*\/\s*)(-\d+)(.*)$/)

  if (!summaryMatch) {
    return renderHighlightedText(summary, query)
  }

  const [, beforeDiff, addedToken, separator, removedToken, afterDiff] = summaryMatch

  return (
    <>
      {renderHighlightedText(beforeDiff, query)}
      <span className={styles.UiConfigResolutionInspector_StepSummaryAdded}>
        {renderHighlightedText(addedToken, query)}
      </span>
      {renderHighlightedText(separator, query)}
      <span className={styles.UiConfigResolutionInspector_StepSummaryRemoved}>
        {renderHighlightedText(removedToken, query)}
      </span>
      {renderHighlightedText(afterDiff, query)}
    </>
  )
}

export default function UiConfigResolutionInspector({ uid }: Props): ReactElement {
  const snap = useSnapshot(uiConfigStore)
  const currentConfig = snap.uiConfigs[uid]
  const selectedPreviewEntry = snap.previewCollection[uid]
  const [expandedStepIds, setExpandedStepIds] = useState<Set<ResolutionStepId>>(new Set())
  const [filterText, setFilterText] = useState<string>('')
  const [debouncedFilterText, setDebouncedFilterText] = useState<string>('')

  const steps = useMemo(
    () =>
      getResolutionChain(uid, {
        uiConfigs: snap.uiConfigs,
        baseUiConfig: snap.baseUiConfig,
        previewCollection: snap.previewCollection,
      }) ?? [],
    [uid, currentConfig, snap.baseUiConfig, selectedPreviewEntry]
  )
  useEffect(() => {
    const trimmedFilter = filterText.trim()
    if (trimmedFilter.length < FILTER_MIN_LENGTH) {
      setDebouncedFilterText('')
      return
    }

    const timeoutId = window.setTimeout(() => {
      setDebouncedFilterText(trimmedFilter)
    }, FILTER_DEBOUNCE_MS)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [filterText])

  useEffect(() => {
    setExpandedStepIds(new Set())
    setFilterText('')
    setDebouncedFilterText('')
  }, [uid])

  const normalizedFilterText = debouncedFilterText.trim().toLowerCase()
  const isFilterActive = normalizedFilterText.length > 0
  const rawStep = steps.find((step) => step.id === 'raw')
  const stepPositionById = useMemo(() => {
    const positions = new Map<ResolutionStepId, number>()
    for (const [index, step] of steps.entries()) {
      positions.set(step.id, index)
    }
    return positions
  }, [steps])
  const stepSummaryById = useMemo(() => {
    const summaries = new Map<ResolutionStepId, string>()
    for (let index = 0; index < steps.length; index += 1) {
      const step = steps[index]
      const previousStep = index > 0 ? steps[index - 1] : undefined
      summaries.set(step.id, getResolutionStepSummary(step, previousStep))
    }
    return summaries
  }, [steps])
  const mutationSteps = useMemo(() => steps.filter((step) => step.id !== 'raw'), [steps])

  const mutationStepMatches = useMemo(() => {
    const matches = new Map<ResolutionStepId, MutationStepMatch>()

    if (!isFilterActive) {
      for (const step of mutationSteps) {
        matches.set(step.id, {
          matches: true,
          matchedChangedFieldNames: [],
        })
      }
      return matches
    }

    for (const step of mutationSteps) {
      const stepIndex = stepPositionById.get(step.id) ?? 0
      const previousStep = stepIndex > 0 ? steps[stepIndex - 1] : undefined
      const sourceLabel = getResolutionStepSource(step.id)
      const label = step.label
      const summary = stepSummaryById.get(step.id) ?? getResolutionStepSummary(step, previousStep)
      const changedFieldsFromRaw = rawStep ? getChangedFieldsFromRaw(rawStep.uiConfig, step.uiConfig) : {}
      const changedFieldNames = Array.from(new Set(collectAllFieldNames(changedFieldsFromRaw)))
      const matchesSourceOrLabelOrSummary = [sourceLabel, label, summary]
        .join(' ')
        .toLowerCase()
        .includes(normalizedFilterText)
      const matchedChangedFieldNames = changedFieldNames.filter((fieldName) =>
        fieldName.toLowerCase().includes(normalizedFilterText)
      )
      const matchesFilter = !isFilterActive || matchesSourceOrLabelOrSummary || matchedChangedFieldNames.length > 0

      matches.set(step.id, {
        matches: matchesFilter,
        matchedChangedFieldNames,
      })
    }

    return matches
  }, [isFilterActive, mutationSteps, normalizedFilterText, rawStep, stepPositionById, stepSummaryById, steps])

  const filteredMutationSteps = useMemo(() => {
    if (!isFilterActive) {
      return mutationSteps
    }

    return mutationSteps.filter((step) => mutationStepMatches.get(step.id)?.matches)
  }, [isFilterActive, mutationStepMatches, mutationSteps])

  const visibleSteps = useMemo(() => {
    if (isFilterActive) {
      return filteredMutationSteps
    }
    if (rawStep) {
      return [rawStep, ...filteredMutationSteps]
    }
    return filteredMutationSteps
  }, [filteredMutationSteps, isFilterActive, rawStep])

  if (!currentConfig) {
    return <div className={styles.UiConfigResolutionInspector_Empty}>{CONFIG_UNAVAILABLE_MESSAGE}</div>
  }

  if (currentConfig.id == null) {
    return <div className={styles.UiConfigResolutionInspector_Empty}>{CONFIG_MALFORMED_RESOLUTION_MESSAGE}</div>
  }

  if (steps.length === 0) {
    return <div className={styles.UiConfigResolutionInspector_Empty}>{CONFIG_NOT_FOUND_MESSAGE}</div>
  }

  function toggleExpanded(stepId: ResolutionStepId): void {
    setExpandedStepIds((prev) => {
      const next = new Set(prev)
      if (next.has(stepId)) {
        next.delete(stepId)
      } else {
        next.add(stepId)
      }
      return next
    })
  }

  return (
    <div className={styles.UiConfigResolutionInspector}>
      {steps.length > 1 && (
        <SearchField
          label="Filter resolution steps"
          helperText="3 characters min"
          value={filterText}
          onChange={(value) => setFilterText(String(value))}
          onClear={() => setFilterText('')}
        />
      )}
      {steps.length === 1 && (
        <p className={styles.UiConfigResolutionInspector_NoTransformations}>
          No transformations were applied to this config.
        </p>
      )}
      {isFilterActive && filteredMutationSteps.length === 0 && (
        <p className={styles.UiConfigResolutionInspector_NoResults}>
          No mutation steps match "{filterText}". Search only applies to changes from raw saved config.
        </p>
      )}
      {visibleSteps.map((step) => {
        const stepIndex = stepPositionById.get(step.id) ?? 0
        const previousStep = stepIndex > 0 ? steps[stepIndex - 1] : undefined
        const isRaw = step.id === 'raw'
        const stepMatch = mutationStepMatches.get(step.id)
        const isExpanded = expandedStepIds.has(step.id)
        const summary = stepSummaryById.get(step.id) ?? getResolutionStepSummary(step, previousStep)
        const currentTrackedJson = stringifyTrackedFields(step.uiConfig)
        const previousTrackedJson = previousStep ? stringifyTrackedFields(previousStep.uiConfig) : currentTrackedJson
        const infoTooltip = getResolutionStepDescription(step.id)
        const sourceLabel = getResolutionStepSource(step.id)
        const matchedChangedFieldNames = stepMatch?.matchedChangedFieldNames ?? []

        return (
          <div key={step.id} className={styles.UiConfigResolutionInspector_Step}>
            <button
              type="button"
              className={styles.UiConfigResolutionInspector_StepHeader}
              onClick={() => toggleExpanded(step.id)}
              aria-expanded={isExpanded}
            >
              <div className={styles.UiConfigResolutionInspector_StepHeaderText}>
                <div className={styles.UiConfigResolutionInspector_StepTitleRow}>
                  <span className={styles.UiConfigResolutionInspector_StepLabel}>
                    {stepIndex + 1}. {renderHighlightedText(step.label, normalizedFilterText)}
                  </span>
                  <span className={styles.UiConfigResolutionInspector_StepSource}>
                    {renderHighlightedText(sourceLabel, normalizedFilterText)}
                  </span>
                </div>
                <span className={styles.UiConfigResolutionInspector_StepSummary}>
                  {renderStepSummary(summary, normalizedFilterText)}
                </span>
                {isFilterActive && matchedChangedFieldNames.length > 0 && (
                  <span className={styles.UiConfigResolutionInspector_MatchSnippet}>
                    Matched changed field{matchedChangedFieldNames.length > 1 ? 's' : ''}:&nbsp;
                    {matchedChangedFieldNames.map((fieldName, fieldIndex) => (
                      <React.Fragment key={`matched-field-${step.id}-${fieldName}`}>
                        {fieldIndex > 0 ? ', ' : ''}
                        {renderHighlightedText(fieldName, normalizedFilterText)}
                      </React.Fragment>
                    ))}
                  </span>
                )}
              </div>
              <div className={styles.UiConfigResolutionInspector_StepHeaderMeta}>
                <span className={styles.UiConfigResolutionInspector_Info} title={infoTooltip} aria-label={infoTooltip}>
                  i
                </span>
                <span className={styles.UiConfigResolutionInspector_Chevron}>{isExpanded ? '▾' : '▸'}</span>
              </div>
            </button>

            {isExpanded && (
              <div className={styles.UiConfigResolutionInspector_StepBody}>
                {isRaw ? (
                  <pre className={styles.UiConfigResolutionInspector_Json}>{currentTrackedJson}</pre>
                ) : (
                  <div className={styles.UiConfigResolutionInspector_Diff}>
                    <MonacoStableDiffEditor
                      original={previousTrackedJson}
                      modified={currentTrackedJson}
                      language="json"
                      height="360px"
                      options={{
                        minimap: { enabled: false },
                        readOnly: true,
                        wordWrap: 'on',
                        automaticLayout: true,
                        renderSideBySide: true,
                        diffCodeLens: true,
                        diffAlgorithm: 'legacy',
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
